import {
  A11yButton,
  FilterChip,
  FilterChips,
  MultipleChoice,
  SearchInput,
  SingleChoice,
} from '@fe-learner-common';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { DateRangeChoice } from './DateRangeChoice';
import { DurationChoice } from './DurationChoice';

interface FilterOption {
  filterKey: string;
  type: 'singleChoice' | 'multipleChoice';
}

export type Duration = {
  min: number;
  max: number;
};

export type DateRange = {
  start?: string | null;
  end?: string | null;
};

interface SingleChoice extends FilterOption {
  type: 'singleChoice';
  selected: string;
  defaultSelected: string;
  label: string;
  items: { key: string; label: string }[];
}

type MultipleChoice = {
  type: 'multipleChoice';
  selectedItems: Set<string>;
  filterKey: string;
  label: string;
  items: { key: string; label: string }[];
};

type DurationChoice = {
  type: 'durationChoice';
  selectedItems: { min: number; max: number };
  filterKey: string;
  label: string;
  items: { min: number; max: number };
};

type DateRangeChoice = {
  type: 'dateRangeChoice';
  selectedItems: DateRange;
  filterKey: string;
  label: string;
  items: DateRange;
};

export type TFilterConfig = {
  search?: {
    value: string;
    placeholder: string;
  };
  options: (MultipleChoice | SingleChoice | DurationChoice | DateRangeChoice)[];
};

type FilterProps = {
  filterConfig: TFilterConfig | null;
  onClearFilter: () => void;
  onChangeFilter: (
    key: string,
    value: Set<string> | string | Duration | DateRange,
  ) => void;
  onViewResults: () => void;
  nrOfResults?: number;
  filterChips?: FilterChip[];
  resultsLoading?: boolean;
};

const handleFilterSet = (set: Set<string>, value: string) => {
  if (!set) return;

  const newSet = new Set(set);
  if (newSet.has(value)) {
    newSet.delete(value);
  } else {
    newSet.add(value);
  }
  return newSet;
};

export const Filter: FC<FilterProps> = ({
  filterConfig,
  onClearFilter,
  onChangeFilter,
  onViewResults,
  nrOfResults,
  filterChips,
  resultsLoading,
}) => {
  const intl = useIntl();
  const [renderPicker, setRenderPicker] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { register, setValue } = useForm();
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      setRenderPicker(true);
    }
  }, [containerRef]);

  const handleClearAll = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }

    onClearFilter();
  };

  const handleSearch = (value: string) => {
    setValue('search', value);
    onChangeFilter('search', value);
  };

  const handleOptionChange = (
    option: SingleChoice | MultipleChoice | DurationChoice | DateRangeChoice,
    value: string | Duration | DateRange,
  ) => {
    let filterValue;
    if (option.type === 'singleChoice') {
      filterValue = value;
    } else if (option.type === 'multipleChoice') {
      filterValue = handleFilterSet(option.selectedItems, value as string);
    } else if (option.type === 'durationChoice') {
      filterValue = value as Duration;
    } else if (option.type === 'dateRangeChoice') {
      filterValue = value as DateRange;
    }
    if (filterValue) {
      setValue(option.filterKey, value);
      onChangeFilter(option.filterKey, filterValue);
    }
  };

  const handleChipRemove = (groupKey: string, filterKey: string) => {
    if (groupKey === 'search') {
      handleSearch('');
      return;
    }

    const option = filterConfig?.options.find((x) => x.filterKey === groupKey);
    if (!option) {
      return;
    }

    if (option.type === 'singleChoice') {
      handleOptionChange(option, option.defaultSelected);
      return;
    }

    handleOptionChange(option, filterKey);
  };

  if (!filterConfig) {
    return null;
  }

  return (
    <div
      className="lf:flex lf:h-full lf:max-h-full lf:flex-col lf:overflow-hidden"
      ref={containerRef}
    >
      <div className="lf:overflow-auto" style={{ scrollbarGutter: 'stable' }}>
        {filterConfig.search && (
          <div className="lf:p-5">
            <SearchInput
              className=""
              {...register('search')}
              id="searchCourses"
              inputRef={searchInputRef}
              defaultValue={filterConfig?.search.value || ''}
              texts={{
                placeholder: filterConfig?.search.placeholder || '',
                clearSearchAriaLabel: intl.formatMessage({
                  id: 'learner.common.search.clearAriaLabel',
                }),
              }}
              onSearch={(value) => {
                handleSearch(value);
              }}
            />
          </div>
        )}

        <div className="lf:space-y-6 lf:p-2">
          {filterConfig.options.map((option) => {
            if (option.type === 'singleChoice') {
              return (
                <SingleChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  onChange={(value: string) => {
                    handleOptionChange(option, value);
                  }}
                  label={option.label}
                  selected={option.selected}
                  items={option.items.map(({ key, label }) => ({
                    key,
                    label,
                  }))}
                />
              );
            }

            if (option.type === 'multipleChoice') {
              return (
                <MultipleChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  onChange={(value: string) => {
                    handleOptionChange(option, value);
                  }}
                  label={option.label}
                  items={option.items.map(({ key, label }) => ({
                    key,
                    label,
                    selected: option.selectedItems?.has(key) ?? false,
                  }))}
                />
              );
            }

            if (option.type === 'durationChoice') {
              return (
                <DurationChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  min={option.items.min}
                  max={option.items.max}
                  value={[
                    option.selectedItems.min || option.items.min,
                    option.selectedItems.max || option.items.max,
                  ]}
                  onChange={(range) => {
                    handleOptionChange(option, {
                      min: range[0],
                      max: range[1],
                    });
                  }}
                  label={option.label}
                  ariaLabelMinValue={intl.formatMessage({
                    id: 'learner.common.filter.hours.minimum',
                  })}
                  ariaLabelMaxValue={intl.formatMessage({
                    id: 'learner.common.filter.hours.maximum',
                  })}
                />
              );
            }

            if (option.type === 'dateRangeChoice' && renderPicker) {
              return (
                <DateRangeChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  containerRef={containerRef}
                  allowedRange={{
                    start: option.items.start,
                    end: option.items.end,
                  }}
                  value={{
                    start: option.selectedItems.start,
                    end: option.selectedItems.end,
                  }}
                  onChange={(range: DateRange) => {
                    handleOptionChange(option, range);
                  }}
                  label={option.label}
                />
              );
            }
          })}
        </div>
      </div>

      <div className="lf:isolate lf:mt-auto lf:flex lf:flex-col lf:border-t lf:border-solid lf:border-neutral-100 lf:p-5">
        <FilterChips
          chips={filterChips}
          nrOfResults={nrOfResults || 0}
          onRemoveFilter={handleChipRemove}
        />
        <div className="lf:relative lf:z-10 lf:flex lf:flex-wrap lf:gap-2">
          <button
            className="lf:l-btn lf:l-btn-md lf:l-btn-secondary lf:flex-1 lf:whitespace-nowrap"
            onClick={handleClearAll}
          >
            {intl.formatMessage({ id: 'learner.common.filter.reset' })}
          </button>
          <A11yButton
            className="lf:l-btn lf:l-btn-md lf:l-btn-primary lf:flex-1 lf:whitespace-nowrap"
            onClick={onViewResults}
            isLoading={resultsLoading}
            disabled={resultsLoading}
          >
            {intl.formatMessage(
              { id: 'learner.common.filter.viewResults' },
              { nr: nrOfResults },
            )}
          </A11yButton>
        </div>
      </div>
    </div>
  );
};
